import { Component } from '@theme/component';

const DRAWER_MOTION_MS = 240;
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'summary',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * CASE.SHE header navigation.
 *
 * The root drawer keeps Horizon's details-based scroll lock and Component event
 * routing. Categories with nested items are native `details` / `summary`
 * disclosures inside the one menu screen, so opening one needs no script, and
 * desktop and mobile share one semantic menu tree and one focus-management
 * implementation.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details
 * @property {HTMLDivElement} menuDrawer
 * @property {HTMLElement} rootScreen
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'rootScreen'];

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  #closing = false;
  #focusTrapActive = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#collapseGroups();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#releaseFocusTrap();
  }

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Opens or closes the root details without allowing the summary's native
   * default action to skip the closing transition.
   *
   * @param {MouseEvent} event
   */
  toggle(event) {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen || this.#closing) return;

    const summary = this.refs.details.querySelector(':scope > summary');
    if (!(summary instanceof HTMLElement)) return;

    this.#previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : summary;
    this.#collapseGroups();

    this.refs.details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    summary.setAttribute('aria-label', Theme.translations.drawer_close);
    this.refs.menuDrawer.setAttribute('aria-hidden', 'false');
    this.#activateFocusTrap();

    requestAnimationFrame(() => {
      this.refs.details.classList.add('menu-open');
      this.#focusCloseButton();
    });
  }

  /**
   * Closes the complete drawer and restores focus to the hamburger.
   *
   * @param {MouseEvent} [event]
   */
  close(event) {
    event?.preventDefault();
    if (!this.isOpen || this.#closing) return;

    this.#closing = true;

    const summary = this.refs.details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.setAttribute('aria-label', Theme.translations.drawer_open);
    this.refs.details.classList.remove('menu-open');

    this.#afterMotion(() => {
      this.#releaseFocusTrap();
      this.refs.details.removeAttribute('open');
      this.refs.menuDrawer.setAttribute('aria-hidden', 'true');
      this.#collapseGroups();

      const focusTarget =
        this.#previouslyFocused?.isConnected && this.#previouslyFocused instanceof HTMLElement
          ? this.#previouslyFocused
          : summary;

      focusTarget?.focus({ preventScroll: true });
      this.#previouslyFocused = null;
      this.#closing = false;
    });
  }

  /** Every category starts collapsed each time the drawer opens. */
  #collapseGroups() {
    this.refs.rootScreen.querySelectorAll('details[open]').forEach((group) => {
      group.removeAttribute('open');
    });
  }

  #focusCloseButton() {
    this.#afterMotion(() => {
      if (!this.isOpen || this.#closing) return;

      const initial = this.refs.rootScreen.querySelector('.menu-drawer__close-button');
      if (initial instanceof HTMLElement) {
        initial.focus({ preventScroll: true });
      }
    });
  }

  #afterMotion(callback) {
    if (this.#reducedMotion.matches) {
      requestAnimationFrame(callback);
      return;
    }

    window.setTimeout(callback, DRAWER_MOTION_MS);
  }

  #activateFocusTrap() {
    if (this.#focusTrapActive) return;

    document.addEventListener('keydown', this.#onDocumentKeyDown);
    document.addEventListener('focusin', this.#onDocumentFocusIn, true);
    this.#focusTrapActive = true;
  }

  #releaseFocusTrap() {
    if (!this.#focusTrapActive) return;

    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    document.removeEventListener('focusin', this.#onDocumentFocusIn, true);
    this.#focusTrapActive = false;
  }

  #onDocumentKeyDown = (event) => {
    if (!this.isOpen || this.#closing) return;

    if (event.key === 'Escape') {
      if (event.isComposing || event.defaultPrevented) return;

      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const screen = this.refs.rootScreen;
    const focusable = this.#getFocusable(screen);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !screen.contains(active))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (active === last || !screen.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };

  #onDocumentFocusIn = (event) => {
    if (!this.isOpen || this.#closing) return;

    const screen = this.refs.rootScreen;
    if (!(event.target instanceof Node)) return;
    if (screen.contains(event.target)) return;

    this.#getFocusable(screen)[0]?.focus({ preventScroll: true });
  };

  /**
   * Returns only currently visible, enabled controls from the menu screen.
   *
   * @param {HTMLElement} screen
   * @returns {HTMLElement[]}
   */
  #getFocusable(screen) {
    return Array.from(screen.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('[hidden], [inert]')) return false;
      // A collapsed category keeps its links in the DOM; only its summary is reachable.
      if (element.closest('details:not([open]) > :not(summary)')) return false;
      if (element.getClientRects().length === 0) return false;

      const style = window.getComputedStyle(element);
      return style.visibility !== 'hidden' && style.display !== 'none';
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
